import React, { createContext, useState, useEffect, useContext } from 'react';
import { UnleashFeatureToggleQuery } from '../generated/graphql';

export enum FeatureToggleEnum {
  OnboardingPromoPage = 'wws-onboarding-welcome-perks',
  AiOnboarding = 'wws-ai-onboarding',
  AiTextButton = 'wws-ai-text-button',
  PaginatedThemeGrid = 'wws-paginated-theme-grid',
  EditorRollout = 'wws-editor-rollout',
}

type FeatureToggleProviderProps = { children: React.ReactNode; featureToggles: FeatureToggleQueryResultItem[] };

interface IFeatureToggleContext {
  isEnabled: (feature: FeatureToggleEnum) => boolean;
}

export type FeatureToggleQueryResultItem = NonNullable<UnleashFeatureToggleQuery['unleashFeatureToggles']>[number];

export const buildFeatureTogglesMap = (
  featureToggles: FeatureToggleQueryResultItem[]
): Map<FeatureToggleEnum, boolean> => {
  const updatedFeatureTogglesMap = new Map<FeatureToggleEnum, boolean>();
  const featureToggleEnumValues = Object.values(FeatureToggleEnum) as string[];

  featureToggles.forEach(toggle => {
    if (toggle && featureToggleEnumValues.includes(toggle.name)) {
      updatedFeatureTogglesMap.set(toggle.name as FeatureToggleEnum, toggle.enabled);
    }
  });

  return updatedFeatureTogglesMap;
};

const FeatureToggleContext = createContext<IFeatureToggleContext | null>(null);

export const FeatureToggleProvider: React.FC<FeatureToggleProviderProps> = ({ children, featureToggles }) => {
  const [featureTogglesMap, setFeatureTogglesMap] = useState<Map<FeatureToggleEnum, boolean>>(
    new Map<FeatureToggleEnum, boolean>()
  );

  useEffect(() => {
    const updatedFeatureTogglesMap = buildFeatureTogglesMap(featureToggles);

    setFeatureTogglesMap(updatedFeatureTogglesMap);
  }, [featureToggles]);

  const isEnabled = (feature: FeatureToggleEnum) => featureTogglesMap.get(feature) || false;

  return (
    <FeatureToggleContext.Provider
      value={{
        isEnabled,
      }}
    >
      {children}
    </FeatureToggleContext.Provider>
  );
};

export const useFeatureTogglesContext = () => {
  const context = useContext(FeatureToggleContext);

  if (!context) {
    throw new Error('useFeatureToggle must be used within FeatureToggleProvider');
  }

  return context;
};
